// src/context/ParametrosAnaliseContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    getParametros,
    saveParametros,
    resetParametros,
    exportConfig,
    importConfig,
} from '../infra/api/ParametrosAnaliseApi';
import { PARAMETROS_DEFAULT } from '../pages/Parametros/mock/parametrosDefault';

const ParametrosAnaliseContext = createContext(null);

export function ParametrosAnaliseProvider({ children }) {
    const [params, setParams] = useState(null);
    const [savedSnapshot, setSavedSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getParametros()
            .then((data) => {
                if (cancelled) return;
                setParams(data);
                setSavedSnapshot(JSON.stringify(data));
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e?.message || 'Falha ao carregar parâmetros');
                const def = structuredClone(PARAMETROS_DEFAULT);
                setParams(def);
                setSavedSnapshot(JSON.stringify(def));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const updateGroup = useCallback((group, patch) => {
        setParams((prev) => ({
            ...prev,
            [group]: Array.isArray(patch) ? patch : { ...prev[group], ...patch },
        }));
    }, []);

    const updateMetrica = useCallback((metrica, patch) => {
        setParams((prev) => ({
            ...prev,
            metricas: {
                ...prev.metricas,
                [metrica]: { ...prev.metricas[metrica], ...patch },
            },
        }));
    }, []);

    const save = useCallback(async () => {
        const data = await saveParametros(params);
        setParams(data);
        setSavedSnapshot(JSON.stringify(data));
        return data;
    }, [params]);

    const reset = useCallback(async () => {
        const data = await resetParametros();
        setParams(data);
        setSavedSnapshot(JSON.stringify(data));
        return data;
    }, []);

    const doExport = useCallback(() => exportConfig(params), [params]);

    const doImport = useCallback(async (file) => {
        const data = await importConfig(file);
        setParams(data);
        // savedSnapshot kept unchanged → isDirty=true → user must click Salvar to persist
    }, []);

    const isDirty = useMemo(
        () => JSON.stringify(params) !== savedSnapshot,
        [params, savedSnapshot]
    );

    const value = useMemo(() => ({
        params,
        loading,
        error,
        isDirty,
        updateGroup,
        updateMetrica,
        save,
        reset,
        doExport,
        doImport,
    }), [params, loading, error, isDirty, updateGroup, updateMetrica, save, reset, doExport, doImport]);

    return (
        <ParametrosAnaliseContext.Provider value={value}>
            {children}
        </ParametrosAnaliseContext.Provider>
    );
}

export function useParametrosAnalise() {
    const ctx = useContext(ParametrosAnaliseContext);
    if (!ctx) throw new Error('useParametrosAnalise must be used within ParametrosAnaliseProvider');
    return ctx;
}
